import {FormEvent, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {Link, Navigate, useLocation} from 'react-router-dom';
import {api, jsonInit} from '../api/client';
import ErrorBox, {FieldError, isGeneralError} from '../components/feedback/ErrorBox';
import Icon from '../components/Icon';
import {useAuth} from '../hooks/useAuth';
import {LoginResponse} from '../types';
import Button from '../components/Button';

// Passwordless sign-in. The first time, two emails arrive: Cloudflare's "verify your address"
// (our mail cannot reach an address until that is accepted), then our sign-in link. The server
// says which step the person is at and this page explains it; spam folders get a mention
// everywhere because mail from a brand-new domain lands there.

const Steps = ({children}: {children: React.ReactNode}) => (
    <ol className="m-0 flex list-none flex-col gap-3 p-0">{children}</ol>
);

const Step = ({n, children}: {n: number; children: React.ReactNode}) => (
    <li className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-shoot-soft font-display text-sm font-bold text-shoot-text">
            {n}
        </span>
        <span className="pt-1 leading-snug">{children}</span>
    </li>
);

const SpamNote = () => (
    <p className="nudge m-0">
        Look in spam or junk if nothing shows up. Mail from sowandtell.garden is new to most inboxes.
    </p>
);

export default function SignInPage() {
    const {isAuthenticated} = useAuth();
    const location = useLocation();
    // From RequireAuth (state) or the OAuth consent page (?redir=/authorize…).
    const from = (location.state as {from?: string} | null)?.from ?? new URLSearchParams(location.search).get('redir');
    const [email, setEmail] = useState('');
    const [result, setResult] = useState<LoginResponse | null>(null);

    const login = useMutation({
        mutationFn: (address: string) =>
            api<LoginResponse>('/api/auth/login-with-email', jsonInit('POST', {email: address, redir: from})),
        onSuccess: setResult,
        meta: {silent: true}
    });

    if (isAuthenticated) {
        return <Navigate to={from ?? '/journal'} replace />;
    }

    const submit = (event: FormEvent) => {
        event.preventDefault();
        login.mutate(email.trim());
    };

    const again = () => {
        setResult(null);
        login.reset();
    };

    const resend = (
        <Button busy={login.isPending} busyLabel="Sending…" onClick={() => login.mutate(email.trim())}>
            I&rsquo;ve done that, send my link
        </Button>
    );

    const differentAddress = (
        <Button variant="secondary" onClick={again}>
            Use a different address
        </Button>
    );

    let body: React.ReactNode;
    if (result?.state === 'link_sent') {
        body = (
            <>
                <h1 className="title m-0">Check your email</h1>
                <p className="m-0 text-ink-2">
                    A sign-in link is on its way to <strong className="text-ink">{email}</strong>. It works once, for the next two hours.
                </p>
                <SpamNote />
                <p className="m-0 text-ink-2">You can close this tab; the link opens the app.</p>
                <div className="flex flex-col gap-2">
                    <Button variant="secondary" busy={login.isPending} busyLabel="Sending…" onClick={() => login.mutate(email.trim())}>
                        Send it again
                    </Button>
                    {differentAddress}
                </div>
            </>
        );
    } else if (result?.state === 'verify_cloudflare') {
        body = (
            <>
                <h1 className="title m-0">One thing first</h1>
                <p className="m-0 text-ink-2">
                    Before we can email <strong className="text-ink">{email}</strong>, Cloudflare (who deliver our mail) need you to confirm
                    it&rsquo;s yours. This happens once.
                </p>
                <Steps>
                    <Step n={1}>
                        Open the email from <strong>Cloudflare</strong> called &ldquo;Verify your email address&rdquo; and tap the link in it.
                    </Step>
                    <Step n={2}>Come back here and tap the button. {result.invited ? 'Your sign-in link follows.' : ''}</Step>
                </Steps>
                <SpamNote />
                {!result.invited && (
                    <p className="m-0 text-ink-2">
                        Sow and Tell is invite-only for now. You&rsquo;re on the list, and once you&rsquo;ve confirmed your address the
                        invite can reach you.
                    </p>
                )}
                <div className="flex flex-col gap-2">
                    {resend}
                    {differentAddress}
                </div>
            </>
        );
    } else if (result?.state === 'waiting_for_invite') {
        body = (
            <>
                <h1 className="title m-0">You&rsquo;re on the list</h1>
                <p className="m-0 text-ink-2">
                    Sow and Tell is an invite-only trial for now. We&rsquo;ve noted <strong className="text-ink">{email}</strong>; an email
                    will arrive when you&rsquo;re in.
                </p>
                <SpamNote />
                {differentAddress}
            </>
        );
    } else if (result?.state === 'throttled') {
        body = (
            <>
                <h1 className="title m-0">Easy there</h1>
                <p className="m-0 text-ink-2">
                    That&rsquo;s a few links in a short time. Check your inbox and spam for one that&rsquo;s already there, or try again in
                    an hour.
                </p>
                {differentAddress}
            </>
        );
    } else if (result?.state === 'send_failed') {
        body = (
            <>
                <h1 className="title m-0">That didn&rsquo;t send</h1>
                <p className="m-0 text-ink-2">
                    Something went wrong emailing <strong className="text-ink">{email}</strong>. Give it a minute and try again; if it keeps
                    happening, tell Jim.
                </p>
                <div className="flex flex-col gap-2">
                    {resend}
                    {differentAddress}
                </div>
            </>
        );
    } else {
        body = (
            <>
                <h1 className="title m-0">Sign in</h1>
                <p className="m-0 text-ink-2">Enter your email and we&rsquo;ll send a link. No password to remember.</p>
                <form onSubmit={submit} className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1">
                        <span className="meta">Email</span>
                        <input
                            className="field"
                            type="email"
                            name="email"
                            autoComplete="email"
                            inputMode="email"
                            autoFocus
                            required
                            placeholder="you@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={login.isPending}
                        />
                    </label>
                    <FieldError error={login.error} field="email" />
                    {isGeneralError(login.error) && <ErrorBox error={login.error} />}
                    <Button type="submit" busy={login.isPending} busyLabel="Sending…" disabled={email.trim() === ''}>
                        Send me a sign-in link
                    </Button>
                </form>
                <div className="rounded-[18px] border-[1.5px] border-dashed border-line p-4">
                    <div className="band-label mb-2 text-ink-2">First time here?</div>
                    <p className="m-0 text-sm text-ink-2">
                        Two emails arrive. The first is from Cloudflare asking you to confirm your address; tap its link. Then come back
                        for your sign-in link. Check spam or junk for both.
                    </p>
                    <p className="m-0 mt-2 text-sm text-ink-2">
                        Not invited yet? Enter your email anyway and you&rsquo;ll go on the list.
                    </p>
                </div>
            </>
        );
    }

    return (
        <div className="settle-in flex flex-col gap-5 pt-2">
            <Link to="/" aria-label="Back" className="-ml-2 flex h-11 w-11 items-center justify-center rounded-pill">
                <Icon name="back" size={22} />
            </Link>
            {body}
        </div>
    );
}
